import {
  LIBRARY_FIELD_ERROR_MESSAGES,
  type LibraryFieldValidationError,
} from '@/features/metadata-library/config/validation';
import type {
  LibraryFormSchema,
  LibraryScalarFormValue,
} from '@/features/metadata-library/types/metadataLibrary';
import { i18n } from '@lingui/core';

export interface LibraryScalarValidationOptions {
  required?: boolean;
}

function isFilled(value: LibraryScalarFormValue): value is string | number {
  if (typeof value === 'number') return true;
  return typeof value === 'string' && value.trim().length > 0;
}

function isMultipleOf(value: number, increment: number): boolean {
  const quotient = value / increment;
  const tolerance = Number.EPSILON * Math.max(1, Math.abs(quotient)) * 8;
  return Math.abs(quotient - Math.round(quotient)) <= tolerance;
}

function validateNumeric(
  formSchema: LibraryFormSchema,
  value: string | number,
): LibraryFieldValidationError | null {
  const parsed = typeof value === 'number' ? value : Number(value.trim());
  if (!Number.isFinite(parsed)) return 'notANumber';
  if (formSchema.type === 'integer' && !Number.isInteger(parsed)) return 'notAnInteger';

  if (typeof formSchema.minimum === 'number' && parsed < formSchema.minimum) return 'tooSmall';
  if (typeof formSchema.exclusiveMinimum === 'number' && parsed <= formSchema.exclusiveMinimum) {
    return 'tooSmall';
  }
  if (typeof formSchema.maximum === 'number' && parsed > formSchema.maximum) return 'tooLarge';
  if (typeof formSchema.exclusiveMaximum === 'number' && parsed >= formSchema.exclusiveMaximum) {
    return 'tooLarge';
  }
  if (typeof formSchema.multipleOf === 'number' && !isMultipleOf(parsed, formSchema.multipleOf)) {
    return 'notAMultiple';
  }

  return null;
}

function validateScalar(
  formSchema: LibraryFormSchema,
  value: LibraryScalarFormValue,
  options: LibraryScalarValidationOptions = {},
): LibraryFieldValidationError | null {
  if (!isFilled(value)) return options.required ? 'required' : null;

  if (Array.isArray(formSchema.enum)) {
    if (typeof value !== 'string' || !formSchema.enum.includes(value)) return 'notAnOption';
  }
  if (formSchema.type === 'integer' || formSchema.type === 'number') {
    return validateNumeric(formSchema, value);
  }

  if (typeof value !== 'string') return 'patternMismatch';
  const length = Array.from(value).length;
  if (typeof formSchema.minLength === 'number' && length < formSchema.minLength) {
    return 'tooShort';
  }
  if (typeof formSchema.maxLength === 'number' && length > formSchema.maxLength) {
    return 'tooLong';
  }
  if (typeof formSchema.pattern === 'string' && formSchema.pattern.length > 0) {
    if (!new RegExp(formSchema.pattern, 'u').test(value)) return 'patternMismatch';
  }

  return null;
}

function validationMessage(error: LibraryFieldValidationError): string {
  return i18n._(LIBRARY_FIELD_ERROR_MESSAGES[error]);
}

/** Validates one optional scalar against the field's runtime catalog schema. */
export function libraryFieldValueError(
  formSchema: LibraryFormSchema,
  value: LibraryScalarFormValue,
  options?: LibraryScalarValidationOptions,
): string | undefined {
  const error = validateScalar(formSchema, value, options);
  return error ? validationMessage(error) : undefined;
}

/** TanStack Form validators shared by every scalar catalog-backed field. */
export function libraryScalarValidators(
  formSchema: LibraryFormSchema,
  options?: LibraryScalarValidationOptions,
) {
  const validate = ({ value }: { value: string }) =>
    libraryFieldValueError(formSchema, value, options);
  return { onBlur: validate, onSubmit: validate };
}

/** Validates one entered property against its schema inside an object-valued field. */
export function libraryObjectPropertyError(
  formSchema: LibraryFormSchema,
  property: string,
  value: string,
): string | undefined {
  const propertySchema = formSchema.properties?.[property];
  if (!propertySchema) return undefined;
  const error = validateScalar(propertySchema, value);
  return error ? validationMessage(error) : undefined;
}

/** Validates required properties and every entered property of an optional object field. */
export function libraryObjectValueError(
  formSchema: LibraryFormSchema,
  values: Record<string, string>,
): string | undefined {
  const entries = Object.entries(values);
  if (!entries.some(([, value]) => isFilled(value))) return undefined;

  const missingRequired = (formSchema.required ?? []).some(
    (property) => Object.hasOwn(values, property) && !isFilled(values[property]),
  );
  if (missingRequired) return validationMessage('incompleteObject');

  for (const [property, value] of entries) {
    if (!isFilled(value)) continue;
    if (formSchema.additionalProperties === false && !formSchema.properties?.[property]) {
      return validationMessage('incompleteObject');
    }
    const error = libraryObjectPropertyError(formSchema, property, value);
    if (error) return error;
  }
  return undefined;
}

/** TanStack Form validators shared by every object-valued catalog field. */
export function libraryObjectValidators(formSchema: LibraryFormSchema) {
  const validate = ({ value }: { value: Record<string, string> }) =>
    libraryObjectValueError(formSchema, value);
  return { onBlur: validate, onSubmit: validate };
}
